import {useEffect, useState, useCallback} from "react";
import toast from "react-hot-toast";
import {useNavigate, useParams} from "react-router-dom";
import {fetchWithAuth} from "../libs/apiUtils";
import { ArrowLeftIcon } from '@heroicons/react/24/solid'
import {Meta} from "../types/global";
import {PencilIcon, TrashIcon, PlusIcon, ListBulletIcon, TableCellsIcon} from "@heroicons/react/24/outline";
import DeleteTableModal from "../components/table/DeleteTableModal";
import EditTableModal from "../components/table/EditTableModal";
import CreateTableModal from "../components/table/CreateTableModal";
import TablePermissionsModal from "../components/table/TablePermissionsModal";
import { KeyIcon } from "@heroicons/react/24/outline";
import Pagination from "../components/Pagination";

interface TableData {
  id: string
  name: string
  description: string
  categoryName?: string
  adminId: string
  isPublic: boolean
  createdByName?: string
}

export default function Table() {
  const { groupId, categoryId } = useParams()

  const [groupName, setGroupName] = useState<string | null>(null)
  const [categoryName, setCategoryName] = useState<string | null>(null)

  // pagination stuff
  const [loading, setLoading] = useState<boolean>(true)
  const [tables, setTables] = useState<TableData[]>([])
  const [meta, setMeta] = useState<Meta | null>(null)
  const [page, setPage] = useState<number>(1)
  const [perPage, setPerPage] = useState<number>(10)

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [toBeDeletedTable, setToBeDeletedTable] = useState<{id?: string, name?: string}>({})

  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [toBeEditedTable, setToBeEditedTable] = useState<{
    id?: string, 
    name?: string, 
    categoryId?: string, 
    description?: string, 
    adminId?: string,
    isPublic?: boolean
  }>({})

  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState<boolean>(false)
  const [toBeManagedTable, setToBeManagedTable] = useState<{id?: string, name?: string}>({})

  const userRoleSlug = localStorage.getItem('user_role_slug')
  const allowedUsers = ['admin', 'super_admin']
  const doesUserHaveAccess = allowedUsers.includes(userRoleSlug ?? '')

  const navigate = useNavigate()

  function navigateBack() {
    navigate(`/data/${groupId}/category`)
  }

  const loadTables = useCallback(async (page: number = 1, categoryId: string) => {
    try {
      setLoading(true)
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/tables`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page, perPage, categoryId }),
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data.message || 'Error fetching table data')
        return
      }

      setTables(data.data || [])
      setMeta(data.meta)
    } catch (err) {
      toast.error('Network error, check your connection')
    } finally {
      setLoading(false)
    }
  }, [perPage])

  async function handleDeleteTable(id: string) {
    try {
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/table/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data[0].message || 'Error deleting table')
        return
      }

      toast.success('Table deleted successfully')
      loadTables(page, categoryId!) 
    } catch (err) {
      toast.error('Network error, check your connection')
    }
  }

  useEffect(() => {
    if (categoryId) loadTables(page, categoryId)
  }, [page, categoryId, loadTables])

  useEffect(() => {
    async function validateGroup() {
      try {
        const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/group-detail`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ groupId }),
        })

        if (!res.ok) {
          throw new Error('Invalid group')
        }

        const data = await res.json()
        setGroupName(data.data.name)
      } catch (err) {
        toast.error('Group not found')
        navigate('/data/group')
      }
    }

    async function validateCategory() {
      try {
        const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/category-detail`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ categoryId }),
        })

        if (!res.ok) {
          throw new Error('Invalid category')
        }

        const data = await res.json()
        setCategoryName(data.data.name)
      } catch (err) {
        toast.error('Category not found')
        navigate(`/data/${groupId}/category`)
      }
    }

    if (groupId) validateGroup()
    if (categoryId) validateCategory()
  }, [groupId, categoryId, navigate])

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <button 
            onClick={navigateBack} 
            className="p-2 hover:bg-neutral-100 rounded-full transition-colors text-neutral-600"
            title="Kembali ke Daftar Kategori"
          >
            <ArrowLeftIcon className="size-6" />
          </button>
          <div>
            <nav className="flex text-sm text-neutral-500 mb-1">
              <button onClick={() => navigate('/data/group')} className="hover:text-primary-600 transition-colors">
                {!doesUserHaveAccess ? 'Akses Data' : 'Manajemen Grup'}
              </button>
              <span className="mx-2">/</span>
              <button onClick={() => navigate(`/data/${groupId}/category`)} className="hover:text-primary-600 transition-colors">{groupName || '...'}</button>
              <span className="mx-2">/</span>
              <span className="text-neutral-900 font-medium">{categoryName}</span>
            </nav>
            <h2 className="text-3xl font-extrabold text-neutral-900 tracking-tight">
              Kategori: <span className="text-primary-600">{categoryName || '...'}</span>
            </h2>
          </div>
        </div>
        
        { doesUserHaveAccess && (
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-xl font-semibold shadow-lg shadow-primary-200 transition-all active:scale-95"
          >
            <PlusIcon className="size-5" />
            Tambah Tabel
          </button>
        )}
      </div>



      <div className="bg-white rounded-2xl shadow-xl shadow-neutral-100 border border-neutral-100 overflow-hidden">
        { loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-neutral-500 font-medium">Memuat data...</p>
          </div>
        ) : tables.length === 0 ? (
          <div className="p-20 text-center">
            <div className="bg-primary-50 size-16 rounded-full flex items-center justify-center mx-auto mb-4 text-primary-600">
              {doesUserHaveAccess ? <PlusIcon className="size-8" /> : <TableCellsIcon className="size-8" />}
            </div>
            <h3 className="text-lg font-bold text-neutral-800">
              {doesUserHaveAccess ? 'Belum Ada Tabel' : 'Tidak Ada Akses Tabel'}
            </h3>
            <p className="text-neutral-500 max-w-xs mx-auto mt-2">
              {doesUserHaveAccess 
                ? 'Silakan klik tombol "Tambah Tabel" untuk memulai pengelolaan data.'
                : 'Anda belum diberikan izin untuk mengakses tabel data dalam kategori ini.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-neutral-50/50">
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider w-20">No</th>
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider">Nama Tabel</th>
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider">Deskripsi</th>
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider">Dibuat Oleh</th>
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider">Akses</th>
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider text-center">
                    {!doesUserHaveAccess ? 'Aksi' : 'Detail'}
                  </th>
                  { doesUserHaveAccess &&
                    <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider w-32 text-center">Aksi</th>
                  }
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {tables.map((table, index) => (
                  <tr 
                    key={table.id} 
                    onClick={() => {
                      if (!doesUserHaveAccess) {
                        navigate(`/data/${table.id}`)
                      }
                    }}
                    className={`group transition-colors ${!doesUserHaveAccess ? 'cursor-pointer hover:bg-primary-50/60' : 'hover:bg-primary-50/30'}`}
                  >
                    <td className="px-6 py-4 text-sm font-medium text-neutral-500">
                      {(meta?.perPage || 20) * (page - 1) + index + 1}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-neutral-900 font-semibold group-hover:text-primary-600 transition-colors">{table.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-neutral-500 line-clamp-2 max-w-xs">{table.description || '-'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-neutral-500">{table.createdByName || '-'}</span>
                    </td>
                    <td className="px-6 py-4">
                      { table.isPublic ? (
                        <div 
                          className="flex items-center justify-center gap-1 text-xs font-bold text-green-600 bg-green-50 px-3 py-1.5 rounded-full"
                        >
                          Publik
                        </div>
                      ) : (
                        <div 
                          className="flex items-center justify-center gap-1 text-xs font-bold text-yellow-600 bg-yellow-50 px-3 py-1.5 rounded-full"
                        >
                          Privat
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        {!doesUserHaveAccess ? (
                          <button 
                            className="flex items-center gap-1 text-xs font-bold text-white bg-primary-600 hover:bg-primary-700 px-4 py-2 rounded-xl shadow-md shadow-primary-200 transition-all"
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/data/${table.id}`)
                            }}
                          >
                            <TableCellsIcon className="size-4" />
                            Lihat Endpoint API & Detail
                          </button>
                        ) : (
                          <>
                            <button 
                              className="flex items-center gap-1 text-xs font-bold text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full hover:bg-primary-100 transition-colors"
                              onClick={() => navigate(`/data/${table.id}/metadata`)}
                            >
                              <ListBulletIcon className="size-4" />
                            </button>
                            <button 
                              className="flex items-center gap-1 text-xs font-bold text-green-600 bg-green-50 px-3 py-1.5 rounded-full hover:bg-green-100 transition-colors"
                              onClick={() => navigate(`/data/${groupId}/${categoryId}/${table.id}`)}
                            >
                              <TableCellsIcon className="size-4" />
                              Konten
                            </button>
                            {doesUserHaveAccess && (
                              <button 
                                title="Izin Akses Pengguna"
                                className="flex items-center gap-1 text-xs font-bold text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-full hover:bg-indigo-100 transition-colors"
                                onClick={() => {
                                  setToBeManagedTable({id: table.id, name: table.name})
                                  setIsPermissionsModalOpen(true)
                                }}
                              >
                                <KeyIcon className="size-4" />
                                Akses
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                    { doesUserHaveAccess && (
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            title="Edit Tabel"
                            className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                            onClick={() => {
                              setToBeEditedTable({
                                id: table.id, 
                                categoryId: categoryId, 
                                name: table.name, 
                                description: table.description,
                                adminId: table.adminId,
                                isPublic: table.isPublic
                              })
                              setIsEditModalOpen(true)
                            }}
                          >
                            <PencilIcon className="size-5" />
                          </button>
                          <button
                            title="Hapus Tabel"
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            onClick={() => {
                              setToBeDeletedTable({id: table.id, name: table.name})
                              setIsDeleteModalOpen(true)
                            }}
                          >
                            <TrashIcon className="size-5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {meta && (
          <div className="px-6 py-4 bg-neutral-50/50 border-t border-neutral-100">
            <Pagination
              currentPage={page}
              lastPage={meta.lastPage}
              total={meta.total}
              perPage={perPage}
              onPageChange={setPage}
              onPerPageChange={(newPerPage) => {
                setPerPage(newPerPage)
                setPage(1)
              }}
            />
          </div>
        )}
      </div>

      <CreateTableModal
        isOpen={isCreateModalOpen}
        setIsOpen={setIsCreateModalOpen}
        categoryId={categoryId!}
        onSuccess={() => loadTables(page, categoryId!)}
      />
      <DeleteTableModal isOpen={isDeleteModalOpen} 
        setIsOpen={setIsDeleteModalOpen} 
        name={toBeDeletedTable.name ?? ''}
        onSubmit={() => handleDeleteTable(toBeDeletedTable.id ?? '')}
      />
      <EditTableModal 
        isOpen={isEditModalOpen}
        setIsOpen={setIsEditModalOpen}
        table={toBeEditedTable}
        onSuccess={() => loadTables(page, categoryId!)}
      />

      <TablePermissionsModal
        isOpen={isPermissionsModalOpen}
        onClose={() => setIsPermissionsModalOpen(false)}
        table={toBeManagedTable}
      />
    </div>
  )
}
